// ════════════════════════════════════════════════════════════
//  online/rooms.js — a match with a friend
// ════════════════════════════════════════════════════════════
//  caP'n: "I want to be able to create a match with a friend but we can
//  decide to watch and not play."
//
//  A ROOM is a little lobby with a 4-letter code. The host picks:
//    · WATCH — two NPC Aenarchists fight (from THE HOURLY's cast, or
//      random teams); everyone in the room watches the same fight.
//    · PLAY  — the host fights one friend (each brings their own
//      team); everyone else watches.
//  Either way everyone can bet Mini Dust on it before it starts.
//
//  games/shatterproof/rooms/{code}:
//    host, server, partyId?, createdAt, mode, red ("host's pick"),
//    blue, rival (uid of the PvP guest), match (current match id),
//    members/{uid}: { name, team?, tomes?, at }
//  games/shatterproof/matches/{id}:
//    spec (see lockstep.js), inputs/, takeover/, result, bets/
//  games/shatterproof/partyRooms/{partyId} = code   (your party finds you)
//
//  ⚠ Rooms and matches are never deleted yet (a sweep is open work).
// ════════════════════════════════════════════════════════════
import { BASE, serverNow } from "./ie.js";
import { matchResult } from "./lockstep.js";
import { ROOM_COUNTDOWN, ROOM_MAX } from "./dials.js";
import { rivalTeam, TEAM_SIZE } from "../sim/battle.js";
import { LINE_IDS } from "../data/roster.js";
import CAST from "../data/hourlyCast.json";

export { ROOM_COUNTDOWN, ROOM_MAX } from "./dials.js";

const LETTERS = "ABCDEFGHJKLMNPQRSTUVWXYZ";
export const roomPath = (code) => `${BASE}/rooms/${code}`;
export const matchPath = (id) => `${BASE}/matches/${id}`;

// ── THE HOURLY's cast, as teams (the League's Circuit cast — circuit.js) ──────
const byName = Object.fromEntries(LINE_IDS.map((id) => [id.replace(/_/g, ""), id]));
// card names → line ids ("H-09" → h09, "Spectral Tiger" → spectral_tiger)
export function teamFromNames(text) {
  const out = [];
  for (const raw of String(text || "").split(",")) {
    const k = raw.toLowerCase().replace(/[^a-z0-9]/g, "");
    if (byName[k]) out.push(byName[k]);
  }
  return out;
}
export const CAST_TEAMS = CAST.entrants.map((e) => ({ n: e.n, name: e.name, team: teamFromNames(e.Beings) }))
  .filter((e) => e.team.length === TEAM_SIZE);
if (CAST_TEAMS.length !== CAST.entrants.length) {
  // eslint-disable-next-line no-console
  console.warn("[shatterproof] some HOURLY cast rows don't name 3 real beings — check gamemaster/IE_HOURLY_TOURNAMENTS.md");
}

const newSeed = () => (Math.floor(Math.random() * 2 ** 31) ^ (serverNow() & 0x7fffffff)) >>> 0 || 1;

export async function createRoom(net, { uid, name, server, partyId }) {
  for (let tries = 0; tries < 12; tries++) {
    let code = "";
    for (let i = 0; i < 4; i++) code += LETTERS[Math.floor(Math.random() * LETTERS.length)];
    const room = {
      host: uid, server, partyId: partyId || null, createdAt: serverNow(), mode: "watch",
      blue: 1, red: 2, rival: null, match: null,
      members: { [uid]: { name, at: serverNow() } },
    };
    const r = await net.txn(roomPath(code), (cur) => (cur ? undefined : room));
    if (r.committed) {
      if (partyId) await net.set(`${BASE}/partyRooms/${partyId}`, code).catch(() => {});
      return { ok: true, code };
    }
  }
  return { ok: false, reason: "Couldn't find a free room code. Try again." };
}

export async function joinRoom(net, { code, uid, name }) {
  code = String(code || "").toUpperCase().replace(/[^A-Z]/g, "").slice(0, 4);
  if (code.length !== 4) return { ok: false, reason: "Room codes are 4 letters." };
  const room = await net.get(roomPath(code));
  if (!room) return { ok: false, reason: `No room ${code}.` };
  const n = Object.keys(room.members || {}).length;
  if (!room.members?.[uid] && n >= ROOM_MAX) return { ok: false, reason: "That room is full." };
  await net.set(`${roomPath(code)}/members/${uid}`, { name, at: serverNow() });
  return { ok: true, code };
}

export async function leaveRoom(net, { code, uid, room }) {
  await net.remove(`${roomPath(code)}/members/${uid}`).catch(() => {});
  if (room && room.host === uid) {
    // hand the room to whoever is left (oldest member), or close it
    const rest = Object.entries(room.members || {}).filter(([u]) => u !== uid).sort((a, b) => (a[1].at || 0) - (b[1].at || 0));
    if (rest.length) await net.update(roomPath(code), { host: rest[0][0], rival: room.rival === rest[0][0] ? null : room.rival }).catch(() => {});
    else {
      await net.remove(roomPath(code)).catch(() => {});
      if (room.partyId) await net.txn(`${BASE}/partyRooms/${room.partyId}`, (cur) => (cur === code ? null : undefined)).catch(() => {});
    }
  }
}

// ── A ROOM'S FIGHT, in two steps ──────────────────────────────
// 1. SET IT UP (host): the match exists with who-fights-whom but NO seed and
//    NO start time. Betting is open. Nobody can know who wins yet.
// 2. START (host): the seed is drawn and the bell set ROOM_COUNTDOWN ahead.
//    Betting closes at the bell.
// Changing the setup voids the old match (its bets refund) and sets up anew.
function buildSpec(room) {
  const members = room.members || {};
  if (room.mode === "play") {
    const guest = room.rival && members[room.rival] ? room.rival : null;
    if (!guest) return { ok: false, reason: "Pick who you're fighting first." };
    const hostTeam = members[room.host]?.team, guestTeam = members[guest]?.team;
    if (!hostTeam || hostTeam.length !== TEAM_SIZE) return { ok: false, reason: "Pick your three beings first." };
    if (!guestTeam || guestTeam.length !== TEAM_SIZE) return { ok: false, reason: `${members[guest].name} hasn't picked their three yet.` };
    return { ok: true, spec: {
      teams: [hostTeam, guestTeam], tomes: [members[room.host].tomes || null, members[guest].tomes || null],
      names: [members[room.host].name, members[guest].name], humans: { 0: room.host, 1: guest },
    } };
  }
  const pick = (n, salt) => {
    const c = CAST_TEAMS.find((e) => e.n === n);
    return c ? { team: c.team, name: c.name } : { team: rivalTeam((serverNow() & 0xffff) + salt), name: "A wandering Aenarchist" };
  };
  const A = pick(room.blue, 11), B = pick(room.red, 23);
  return { ok: true, spec: { teams: [A.team, B.team], tomes: [null, null], names: [A.name, B.name], humans: {} } };
}

export async function setupRoomMatch(net, { code, room }) {
  const built = buildSpec(room);
  if (!built.ok) return built;
  if (room.match) await voidRoomMatch(net, room.match);
  const spec = { ...built.spec, kind: "room", room: code, createdAt: serverNow() };
  const id = `r${code}${Date.now().toString(36)}`;
  await net.set(`${matchPath(id)}/spec`, spec);
  await net.update(roomPath(code), { match: id });
  return { ok: true, id };
}
export async function voidRoomMatch(net, id) {
  // only a match that hasn't started can be called off
  await net.txn(`${matchPath(id)}/spec`, (cur) => (cur && cur.startAt == null ? { ...cur, void: true } : undefined)).catch(() => {});
}
export async function startRoomMatch(net, { room }) {
  if (!room.match) return { ok: false, reason: "Set the fight up first." };
  const seed = newSeed(), startAt = serverNow() + ROOM_COUNTDOWN * 1000;
  const r = await net.txn(`${matchPath(room.match)}/spec`, (cur) => (cur && !cur.void && cur.startAt == null ? { ...cur, seed, startAt } : undefined));
  return r.committed ? { ok: true, id: room.match } : { ok: false, reason: "That fight already started (or was called off)." };
}

/** The result a bet pool settles against (pool = <match>/bets): winner, refund rules. */
export const poolResultOf = (net) => async (poolPath) => {
  const base = poolPath.replace(/\/bets[^/]*$/, "");
  const r = await matchResult(net, base, serverNow());
  if (!r) return null;
  const spec = (await net.get(`${base}/spec`)) || {};
  return { winner: r.winner, void: !!r.void, lockAt: spec.startAt ?? Infinity, keys: spec.keys || null };
};
